import { useId, type ReactElement } from 'react';
import type { BitrixConnection } from '@chatapp-inbox/shared';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { isPublicHttps, webhookUrl } from './webhook-url';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './BitrixSection.module.css';

export const COPIED_TEXT = 'Endereço copiado.';
export const COPY_FAILED_TEXT = 'Não foi possível copiar. Selecione o endereço e copie à mão.';
export const NOT_PUBLIC_TEXT =
  'Este endereço não é público com HTTPS (é local ou usa HTTP simples), então o Bitrix não consegue chamá-lo. Abra o CladvApp pelo endereço público do servidor para instalar o aplicativo.';

function CopyField({
  label,
  address,
  onCopied,
}: {
  label: string;
  address: string;
  onCopied: (ok: boolean) => void;
}): ReactElement {
  const inputId = useId();
  const copy = (): void => {
    // Sem permissão ou sem HTTPS o navegador recusa: a pessoa ainda pode selecionar e copiar à mão.
    if (!navigator.clipboard) return onCopied(false);
    navigator.clipboard.writeText(address).then(
      () => onCopied(true),
      () => onCopied(false),
    );
  };
  return (
    <div className={forms.field}>
      <label htmlFor={inputId} className={forms.label}>
        {label}
      </label>
      <div className={styles.row}>
        <input
          id={inputId}
          className={forms.input}
          readOnly
          value={address}
          spellCheck={false}
          onFocus={(event) => event.currentTarget.select()}
        />
        <Button onClick={copy}>Copiar</Button>
      </div>
    </div>
  );
}

/**
 * O passo a passo para criar o aplicativo local no Bitrix24, com os dois endereços do CladvApp que o
 * cadastro pede (a chave de instalação vai neles: só quem tem este painel os conhece). Ao salvar o
 * aplicativo o Bitrix o instala e manda os dados para cá; o ID e a chave só aparecem DEPOIS disso.
 * Aparece até a conexão estar completa.
 */
export function BitrixInstallSection({
  connection,
}: {
  connection: BitrixConnection;
}): ReactElement {
  const headingId = useId();
  const status = useStatusMessage();
  const { installPath, handlerPath } = connection;
  if (!installPath || !handlerPath) return <></>;
  const install = webhookUrl(installPath);
  const handler = webhookUrl(handlerPath);
  const done = (ok: boolean): void => status.announce(ok ? COPIED_TEXT : COPY_FAILED_TEXT);
  const missingCredentials = !connection.clientId || !connection.hasSecret;

  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 id={headingId} className={section.cardTitle}>
        Instalar o aplicativo no Bitrix
      </h3>
      <p className={forms.hint}>
        Faça isto uma vez, no Bitrix24, com um usuário administrador do portal. O aplicativo age com
        as permissões de quem o instala: use alguém que veja e crie Leads. Os nomes dos menus podem
        variar um pouco conforme a versão.
      </p>
      <ol className={styles.steps}>
        <li>
          No Bitrix24, abra <strong>Recursos para desenvolvedores</strong>, na aba{' '}
          <strong>Casos de uso comuns</strong> escolha <strong>Outros</strong> e{' '}
          <strong>Aplicativo local</strong>, e marque a opção <strong>Servidor</strong>.
        </li>
        <li>
          Em <strong>Caminho do manipulador</strong> (ou “Your handler path”), cole o primeiro
          endereço abaixo. Em <strong>Caminho da instalação inicial</strong> (ou “Initial
          installation path”), cole o segundo.
        </li>
        <li>
          Deixe <strong>vazios</strong> os campos do texto do item de menu e{' '}
          <strong>desmarcada</strong> a opção “O aplicativo conclui a instalação sozinho”: o
          aplicativo não tem tela.
        </li>
        <li>
          Em <strong>Permissões</strong>, marque <strong>CRM</strong> e <strong>Usuários</strong> (a
          versão mínima basta) e salve. O Bitrix instala o aplicativo e envia os dados para cá: o
          selo “Situação” acima vira “Conectado” em alguns segundos.
        </li>
        <li>
          O Bitrix mostra o <strong>ID do aplicativo</strong> e a{' '}
          <strong>chave do aplicativo</strong>: digite-os nos campos acima e salve.
        </li>
      </ol>
      <CopyField label="Caminho do manipulador" address={handler} onCopied={done} />
      <CopyField label="Caminho da instalação inicial" address={install} onCopied={done} />
      {!isPublicHttps(install) && (
        <p role="note" className={styles.notice}>
          {NOT_PUBLIC_TEXT}
        </p>
      )}
      {connection.status === 'awaiting_install' && (
        <p role="status" className={forms.hint}>
          Aguardando o Bitrix enviar a instalação…
        </p>
      )}
      {connection.status === 'connected' && missingCredentials && (
        <p role="status" className={forms.hint}>
          O Bitrix já instalou o aplicativo. Falta digitar o ID e a chave do aplicativo (último
          passo) para ligar a integração.
        </p>
      )}
      {connection.status === 'error' && (
        <p role="status" className={forms.hint}>
          Para reconectar, clique em Reinstalar no cartão do aplicativo no Bitrix: ele envia a
          instalação outra vez.
        </p>
      )}
      <StatusRegion message={status.message} />
    </section>
  );
}
